import type { StreetSegment } from '../domain/streetSnap'
import type {
  AiFlagRun,
  AiProviderStatus,
  CreateAiFlagRunCommand,
  ProcessAiFlagRunResult,
  RouteAiSettings,
  SaveRouteAiSettingsCommand,
  SetFlagDecisionCommand,
  VerifyModelResult,
} from '../domain/aiFlagRun'
import type {
  ApplyFlagsCommand,
  CompleteFlagRunCommand,
  CreateFlagRunCommand,
  FlagRunLocation,
  StreetSegmentsStatus,
  ApplyRouteToolCommand,
  ApplyRouteToolResult,
  OptimizeRoutesCommand,
  OptimizeRoutesResult,
  RevertRouteToolCommand,
  RevertRouteToolResult,
  RouteToolRunDetail,
  RouteToolRunListItem,
} from '../domain/routeTools'

/**
 * Outils Routes (docs/routes/README.md §8) : calcul par la fonction Edge `route-optimization`, puis traitements,
 * application et annulation par RPC. Jamais de repli sur des données de démonstration : un résultat inventé
 * passerait pour un vrai calcul.
 */
export interface RouteToolsRepository {
  /** Le service d'optimisation est-il configuré côté serveur ? (aucun appel facturable) */
  getOptimizationStatus: () => Promise<{ configured: boolean }>
  optimize: (command: OptimizeRoutesCommand) => Promise<OptimizeRoutesResult>
  listRuns: (season: string) => Promise<RouteToolRunListItem[]>
  getRun: (runId: string) => Promise<RouteToolRunDetail>
  apply: (command: ApplyRouteToolCommand) => Promise<ApplyRouteToolResult>
  revert: (command: RevertRouteToolCommand) => Promise<RevertRouteToolResult>
  cancel: (runId: string) => Promise<void>

  // Rapprocher les drapeaux (lot C) : calcul dans l'écran, sans coût ; la base vérifie et enregistre.
  getStreetSegmentsStatus: () => Promise<StreetSegmentsStatus>
  getStreetSegmentsNear: (locationIds: string[]) => Promise<StreetSegment[]>
  createFlagRun: (
    command: CreateFlagRunCommand,
  ) => Promise<{ runId: string; replayed: boolean; locations: FlagRunLocation[] }>
  completeFlagRun: (command: CompleteFlagRunCommand) => Promise<void>
  applyFlags: (command: ApplyFlagsCommand) => Promise<ApplyRouteToolResult>
  revertFlags: (command: RevertRouteToolCommand) => Promise<RevertRouteToolResult>

  // Drapeaux ajustés par l'IA (lot E) : fonction Edge `route-flags-ai` (Gemini, clé côté serveur) et RPC. Application
  // et annulation : `applyFlags` / `revertFlags` (seules les décisions retenues sont acceptées par la base).
  getAiSettings: () => Promise<RouteAiSettings>
  saveAiSettings: (command: SaveRouteAiSettingsCommand) => Promise<RouteAiSettings>
  /** La clé Gemini est-elle présente côté serveur ? (aucun appel facturable) */
  getAiProviderStatus: () => Promise<AiProviderStatus>
  /** Vérifie le modèle enregistré : description + un appel facturable sur une image synthétique. */
  verifyAiModel: () => Promise<VerifyModelResult>
  createAiFlagRun: (command: CreateAiFlagRunCommand) => Promise<{ runId: string; replayed: boolean }>
  /** Fait avancer l'analyse (quelques résidences, en parallèle) ; à rappeler tant qu'il reste du travail. */
  processAiFlagRun: (runId: string) => Promise<ProcessAiFlagRunResult>
  getAiFlagRun: (runId: string) => Promise<AiFlagRun>
  /** Dernier traitement IA en cours ou en revue sur la route, sinon null. */
  getActiveAiFlagRun: (routeId: string) => Promise<AiFlagRun | null>
  stopAiFlagRun: (runId: string) => Promise<void>
  resumeAiFlagRun: (runId: string, scope: 'STOPPED' | 'ERROR' | 'BOTH') => Promise<void>
  setFlagDecision: (command: SetFlagDecisionCommand) => Promise<void>
}

export class RouteToolError extends Error {
  readonly code: string

  constructor(code: string) {
    super(code)
    this.name = 'RouteToolError'
    this.code = code
  }
}
